/**
 * List View Content Service
 *
 * Single source of truth for what the listing sheet panel shows per active chip: whether the
 * Floor Plans / Quick Move-Ins tab bar renders, and how to render the panel's content on mount,
 * chip switch, tab switch, or house-plans/housing-units data changes. Mirrors
 * services/listingTitleService.js's per-chip lookup pattern — add a new chip's panel behavior
 * here instead of branching on it inside plugins/listView.plugin.js.
 *
 * @module nativeInteractiveSiteMap/services/listViewContentService
 */

import LotStatus from '../enums/lotStatus';
import { renderFloorPlanGrid } from '../components/floorPlanCards.dom';
import renderComingSoonPanel from '../components/comingSoonPanel.dom';
import { renderQuickMoveInGrid } from '../components/quickMoveInCards.dom';
import renderUnavailableLotsPanel from '../components/unavailableLots.dom';
import {
  selectSelectedTab,
  selectFilteredHousePlans,
  selectFilteredHousingUnits,
  selectSelectedLotHousePlanIds,
} from '../redux/selectors';
import { TAB_FLOOR_PLANS, TAB_QUICK_MOVE_INS } from '../constants';

/**
 * Creates a renderer wrapper that displays an empty state panel when no cards are available,
 * otherwise delegates to the provided grid renderer.
 *
 * @param {Function} gridRenderer - The grid renderer function (e.g., renderFloorPlanGrid)
 * @returns {Function} A renderer function that accepts cards array and returns HTML
 */
function createGridRendererWithEmptyState(gridRenderer) {
  return (cards) => {
    if (!Array.isArray(cards) || cards.length === 0) {
      return renderUnavailableLotsPanel();
    }
    return gridRenderer(cards);
  };
}

function renderAvailableContent(storeState, { renderCards }) {
  if (selectSelectedTab(storeState) === TAB_QUICK_MOVE_INS) {
    renderCards(
      selectFilteredHousingUnits(storeState),
      createGridRendererWithEmptyState(renderQuickMoveInGrid)
    );
    return;
  }
  renderCards(
    selectFilteredHousePlans(storeState),
    createGridRendererWithEmptyState(renderFloorPlanGrid)
  );
}

function renderAvailableOnHousePlansChanged(storeState, renderContext) {
  if (selectSelectedTab(storeState) !== TAB_FLOOR_PLANS) {
    return;
  }
  renderAvailableContent(storeState, renderContext);
}

function renderAvailableOnHousingUnitsChanged(storeState, renderContext) {
  if (selectSelectedTab(storeState) !== TAB_QUICK_MOVE_INS) {
    return;
  }
  renderAvailableContent(storeState, renderContext);
}

function renderComingSoonContent(storeState, { renderCards }) {
  const priorityPlanIds = selectSelectedLotHousePlanIds(storeState);
  renderCards(
    selectFilteredHousePlans(storeState),
    createGridRendererWithEmptyState((cards) => renderComingSoonPanel(cards, { priorityPlanIds }))
  );
}

function renderDefaultContent(storeState, { renderCards }) {
  renderCards(
    selectFilteredHousePlans(storeState),
    createGridRendererWithEmptyState(renderFloorPlanGrid)
  );
}

const LIST_VIEW_CONTENT = {
  [LotStatus.Available.toString()]: {
    hasTabs: true,
    renderContent: renderAvailableContent,
    renderOnHousePlansChanged: renderAvailableOnHousePlansChanged,
    renderOnHousingUnitsChanged: renderAvailableOnHousingUnitsChanged,
    renderOnSelectedLotChanged: () => {},
  },
  [LotStatus.ComingSoon.toString()]: {
    hasTabs: false,
    renderContent: renderComingSoonContent,
    renderOnHousePlansChanged: renderComingSoonContent,
    renderOnHousingUnitsChanged: () => {},
    renderOnSelectedLotChanged: renderComingSoonContent,
  },
};

/**
 * Fallback for chips without dedicated panel content yet (e.g. Model, Sold): plain floor-plan
 * grid, no tabs, no chip-specific UI. Matches the plugin's pre-existing behavior for those chips.
 */
const DEFAULT_CONTENT = {
  hasTabs: false,
  renderContent: renderDefaultContent,
  renderOnHousePlansChanged: renderDefaultContent,
  renderOnHousingUnitsChanged: () => {},
  renderOnSelectedLotChanged: () => {},
};

/**
 * @param {string} chipKey - the active chip key, e.g. from redux/selectors.js#selectActiveChip
 * @returns {{
 *   hasTabs: boolean,
 *   renderContent: (storeState: object, renderContext: { renderCards: Function }) => void,
 *   renderOnHousePlansChanged: (storeState: object, renderContext: { renderCards: Function }) => void,
 *   renderOnHousingUnitsChanged: (storeState: object, renderContext: { renderCards: Function }) => void,
 *   renderOnSelectedLotChanged: (storeState: object, renderContext: { renderCards: Function }) => void,
 * }}
 */
export default function getListViewContent(chipKey) {
  return LIST_VIEW_CONTENT[chipKey] ?? DEFAULT_CONTENT;
}
